import { useEffect, useRef, useState } from 'react';
import { pushVenuePath, syncShellHistoryFromLocation } from '../shellHistory';
import { searchVenues, type VenueSearchResult } from '../searchApi';
import { VenueCardMini } from './VenueCardMini';
import { HopperIcon } from './icons/HopperIcon';

type SearchState = 'idle' | 'loading' | 'success' | 'empty' | 'error';
type SearchMode = 'desktop' | 'mobile';

type AnchorRect = {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
};

const SEARCH_INPUT_SELECTOR = '.global-search input[type="search"], .mobile-search input[type="search"]';
const SEARCH_DEBOUNCE_MS = 400;

function rectFor(input: HTMLInputElement): AnchorRect {
  const rect = input.getBoundingClientRect();
  return {
    top: rect.top,
    left: rect.left,
    right: rect.right,
    bottom: rect.bottom,
    width: rect.width,
    height: rect.height,
  };
}

function modeFor(input: HTMLInputElement): SearchMode {
  return input.closest('.mobile-search') ? 'mobile' : 'desktop';
}

function isSearchInput(target: EventTarget | null): target is HTMLInputElement {
  return target instanceof HTMLInputElement && target.matches(SEARCH_INPUT_SELECTOR);
}

export function SearchUiBridge() {
  const activeInputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState('');
  const [state, setState] = useState<SearchState>('idle');
  const [results, setResults] = useState<VenueSearchResult[]>([]);
  const [active, setActive] = useState(false);
  const [mode, setMode] = useState<SearchMode>('desktop');
  const [anchor, setAnchor] = useState<AnchorRect | null>(null);

  const refreshAnchor = () => {
    const input = activeInputRef.current;
    if (!input || !document.contains(input)) {
      setAnchor(null);
      return;
    }
    setAnchor(rectFor(input));
  };

  useEffect(() => {
    const activate = (input: HTMLInputElement) => {
      activeInputRef.current = input;
      setQuery(input.value);
      setMode(modeFor(input));
      setAnchor(rectFor(input));
      setActive(true);
    };

    const handleFocus = (event: FocusEvent) => {
      if (isSearchInput(event.target)) {
        activate(event.target);
      }
    };

    const handleInput = (event: Event) => {
      if (isSearchInput(event.target)) {
        activate(event.target);
      }
    };

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(SEARCH_INPUT_SELECTOR) || target.closest('.hopper-search-overlay') || target.closest('.hopper-search-clear')) {
        return;
      }
      setActive(false);
    };

    const handleViewportChange = () => refreshAnchor();

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('input', handleInput);
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('input', handleInput);
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, []);

  useEffect(() => {
    const normalized = query.trim();

    if (!active || normalized.length < 2) {
      setState('idle');
      setResults([]);
      return;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setState('loading');
      searchVenues(normalized, controller.signal)
        .then((items) => {
          setResults(items);
          setState(items.length > 0 ? 'success' : 'empty');
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          setResults([]);
          setState('error');
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [active, query]);

  const clearSearch = () => {
    const input = activeInputRef.current;
    if (input) {
      input.value = '';
      input.focus();
      setAnchor(rectFor(input));
    }
    setQuery('');
    setResults([]);
    setState('idle');
    setActive(true);
  };

  const openVenue = (venueId: string) => {
    setActive(false);
    pushVenuePath(venueId);
    syncShellHistoryFromLocation();
  };

  const normalized = query.trim();
  const showPanel = active && anchor !== null && normalized.length >= 2;
  const showClear = active && anchor !== null && query.length > 0;
  const panelStyle = anchor ? (
    mode === 'mobile'
      ? { top: anchor.bottom + 8, left: 12, width: Math.max(0, window.innerWidth - 24) }
      : { top: anchor.bottom + 8, left: anchor.left, width: Math.max(anchor.width, 360) }
  ) : undefined;
  const clearStyle = anchor ? {
    top: anchor.top + Math.max(0, (anchor.height - 30) / 2),
    left: anchor.right - 36,
  } : undefined;

  return (
    <>
      {showClear && (
        <button
          className="hopper-search-clear"
          type="button"
          aria-label="Очистить поиск"
          style={clearStyle}
          onClick={clearSearch}
        >
          <HopperIcon name="close" size={17} />
        </button>
      )}

      {showPanel && (
        <section
          className={`hopper-search-overlay hopper-search-overlay--${mode}`}
          style={panelStyle}
          aria-label="Результаты поиска"
        >
          {state === 'loading' && (
            <div className="hopper-search-skeletons" aria-label="Ищем заведения" role="status">
              {[0, 1, 2].map((item) => (
                <div className="hopper-search-skeleton" key={item}>
                  <span className="hopper-skeleton hopper-skeleton--rect" />
                </div>
              ))}
            </div>
          )}

          {state === 'success' && (
            <div className="hopper-search-results">
              {results.map((venue) => (
                <VenueCardMini
                  key={venue.id}
                  name={venue.name}
                  address={venue.address}
                  imageUrl={venue.cover_url}
                  showAddressPin
                  showMeta={false}
                  onClick={() => openVenue(venue.id)}
                />
              ))}
            </div>
          )}

          {state === 'empty' && (
            <div className="hopper-search-state" role="status">
              <HopperIcon name="search" size={22} />
              <strong>Ничего не найдено по вашему запросу</strong>
            </div>
          )}

          {state === 'error' && (
            <div className="hopper-search-state hopper-search-state--error" role="alert">
              <HopperIcon name="error" size={22} />
              <strong>Не удалось выполнить поиск</strong>
              <span>Проверьте соединение и попробуйте ещё раз.</span>
            </div>
          )}
        </section>
      )}
    </>
  );
}
